const SearchableSelect = {
    init() {
        const searchableSelects = document.querySelectorAll("select.searchable-select")

        searchableSelects.forEach(select => {
            this.createInstance(select)
        })

        // Global listener: when clicking anywhere outside, close all dropdowns
        document.addEventListener("mousedown", () => {
            this.closeAllDropdowns()
        })

        document.addEventListener("searchable-select:refresh", (event) => {
            this.refreshSelectOptions(event.target)
        })
    },

    refreshSelectOptions(select) {
        const container = select.parentElement
        const input = container.querySelector(".searchable-select-input")
        const dropdown = container.querySelector(".searchable-select-dropdown")

        // Refresh dropdown options
        this.populateDropdown(select, dropdown, input, container)
        select.value = ""
        input.value = ""
    },

    // Orchestrates the creation of one searchable select instance
    createInstance(select) {
        // Hide the native select element
        select.classList.add("hidden-but-focusable")

        // Create and append the container
        const container = this.createContainer(select)

        // Create and append the input field
        const input = this.createInput(select)
        container.appendChild(input)

        // Create and append the dropdown list (ul)
        const dropdown = this.createDropdown()
        container.appendChild(dropdown)

        const clearBtn = this.createClearButton(select, input, dropdown, container)
        container.appendChild(clearBtn)

        // Attach events to the container (e.g. mousedown to close others)
        this.attachContainerEvents(container)

        // Populate the dropdown with options from the native select
        this.populateDropdown(select, dropdown, input, container, clearBtn)

        // Attach events to the input (opening dropdown, filtering, keyboard nav)
        this.attachInputEvents(input, dropdown, select, container)

        this.toggleClearButton(input, clearBtn)
    },

    createClearButton(select, input, dropdown, container) {
        const btn = document.createElement("button")

        btn.type = "button"
        btn.className = "searchable-select-clear hidden"
        btn.innerHTML = "Clear"

        btn.addEventListener("mousedown", (e) => {
            e.preventDefault()
            e.stopPropagation()

            // reset state
            input.value = ""
            select.value = ""

            const childName = select.dataset.parentOf

            if (childName) {
                const childSelect = document.querySelector(
                    `select[name*="${childName}"]`
                )

                if (childSelect) {
                    childSelect.value = ""

                    childSelect.dispatchEvent(new Event("change", { bubbles: true }))
                    childSelect.dispatchEvent(new Event("input", { bubbles: true }))
                    childSelect.dispatchEvent(
                        new CustomEvent("searchable-select:refresh", { bubbles: true })
                    )
                }
            }

            // reset options selection
            select.querySelectorAll("option[selected]").forEach(o => {
                o.removeAttribute("selected")
            })

            // reset UI
            dropdown.classList.add("hidden")
            container.activeIndex = -1

            // trigger updates
            select.dispatchEvent(new Event("change", { bubbles: true }))
            select.dispatchEvent(new Event("input", { bubbles: true }))
            select.dispatchEvent(
                new CustomEvent("searchable-select:change", { bubbles: true })
            )

            btn.classList.add("hidden")
        })

        return btn
    },

    toggleClearButton(input, btn) {
        if (!btn) return
        if (input.value.trim() !== "") {
            btn.classList.remove("hidden")
        } else {
            btn.classList.add("hidden")
        }
    },

    // Creates the container element and inserts it before the select
    createContainer(select) {
        const container = document.createElement("div")
        container.className = "searchable-select-container"
        container.activeIndex = -1
        select.parentNode.insertBefore(container, select)
        return container
    },

    // Creates the input element, sets its placeholder, and saves the original placeholder
    createInput(select) {
        const placeholder = select.getAttribute("data-placeholder") ||
            document.querySelector('meta[name="localized-select-prompt"]')?.getAttribute("content") ||
            "Select option..."

        const input = document.createElement("input")
        input.type = "text"
        input.className = "searchable-select-input"
        input.placeholder = placeholder
        input.dataset.originalPlaceholder = input.placeholder
        input.value =
            select.value === ""
                ? ""
                : select.querySelector(`option[value="${select.value}"]`).textContent
        return input
    },

    // Creates the dropdown list (ul) element
    createDropdown() {
        const dropdown = document.createElement("ul")
        dropdown.className = "searchable-select-dropdown hidden"
        return dropdown
    },

    // Attaches a mousedown event on the container to close other instances.
    attachContainerEvents(container) {
        container.addEventListener("mousedown", (e) => {
            this.closeAllExcept(container)
            e.stopPropagation()
        })
    },

    // Clears and repopulates the dropdown based on the current options in the native select
    populateDropdown(select, dropdown, input, container, clearBtn) {
        // Clear existing options
        dropdown.innerHTML = ""
        Array.from(select.options).forEach((option, index) => {
            // Skip the first option if it's empty
            if (index === 0 && option.value.trim() === "") {
                return
            }
            const li = document.createElement("li")
            li.className = "searchable-select-option"
            li.dataset.originalText = option.textContent
            li.dataset.value = option.value
            li.textContent = option.textContent
            dropdown.appendChild(li)

            // Option selection via mousedown event
            li.addEventListener("mousedown", () => {
                input.value = option.textContent
                select.value = option.value

                this.toggleClearButton(input, clearBtn)

                dropdown.classList.add("hidden")
                container.activeIndex = -1

                // Ensure only one option is selected at a time
                select.querySelectorAll(`option[selected]:not([value="${option.value}"])`).forEach(o => o.removeAttribute("selected"))
                select.querySelector(`option[value="${option.value}"]`)?.setAttribute("selected", "")

                // 🔥 Dispatch both "change" and "input" events to properly update the model selection
                select.dispatchEvent(new Event("change", {bubbles: true}))
                select.dispatchEvent(new Event("input", {bubbles: true}))

                // 🔥 Dispatch a custom event to ensure compatibility
                select.dispatchEvent(new CustomEvent("searchable-select:change", {bubbles: true}))
            })
        })
    },

    // Attaches event listeners to the input for opening the dropdown, filtering, and keyboard navigation.
    attachInputEvents(input, dropdown, select, container) {
        // Handler to open dropdown, adjust positioning and update placeholder
        const openDropdown = (e) => {
            this.closeAllExcept(container)
            e.stopPropagation()
            dropdown.classList.remove("hidden")
            this.adjustDropdownPosition(container, dropdown)

            if (!input.value) {
                input.placeholder = this.getLocalizedTypeToSearch()
            }
            if (this.isValidSelection(input.value, select)) {
                this.showAllOptions(input.value, dropdown)
            } else {
                this.filterOptions(input.value, dropdown)
            }
        }

        input.addEventListener("mousedown", openDropdown)
        input.addEventListener("focus", openDropdown)

        input.addEventListener("blur", () => {
            if (!this.isValidSelection(input.value, select)) {
                input.value = ""
                select.value = ""
                this.toggleClearButton(input, clearBtn)
                select.dispatchEvent(new Event("change", {bubbles: true}))
                select.dispatchEvent(new Event("input", {bubbles: true}))
                select.dispatchEvent(new CustomEvent("searchable-select:change", {bubbles: true}))
            }
        })

        // Keyup: when typing (except navigation keys), reset active index and filter
        input.addEventListener("keyup", (e) => {
            if (
                e.key !== "ArrowDown" &&
                e.key !== "ArrowUp" &&
                e.key !== "Enter" &&
                e.key !== "Escape"
            ) {
                container.activeIndex = -1
                this.filterOptions(input.value, dropdown)
                this.toggleClearButton(input, clearBtn)
            }
        })

        // Keydown: handle arrow navigation, Enter selection, and Escape to close
        input.addEventListener("keydown", (e) => {
            if (["ArrowDown", "ArrowUp", "Enter", "Escape"].includes(e.key)) {
                e.preventDefault()
            }
            const visibleOptions = Array.from(dropdown.children).filter(li => {
                return !li.classList.contains("hidden")
            })
            if (visibleOptions.length === 0) {
                return
            }
            if (e.key === "ArrowDown") {
                if (container.activeIndex < visibleOptions.length - 1) {
                    container.activeIndex++
                }
                this.updateActiveOption(visibleOptions, container.activeIndex)
            } else if (e.key === "ArrowUp") {
                if (container.activeIndex > 0) {
                    container.activeIndex--
                }
                this.updateActiveOption(visibleOptions, container.activeIndex)
            } else if (e.key === "Enter") {
                if (
                    container.activeIndex >= 0 &&
                    container.activeIndex < visibleOptions.length
                ) {
                    visibleOptions[container.activeIndex].dispatchEvent(
                        new Event("mousedown")
                    )
                }
            } else if (e.key === "Escape") {
                dropdown.classList.add("hidden")
                container.activeIndex = -1
                if (!input.value) {
                    input.placeholder = input.dataset.originalPlaceholder
                }
            }
        })
    },

    // Adjusts the dropdown position: opens upward if not enough space below.
    adjustDropdownPosition(container, dropdown) {
        const rect = container.getBoundingClientRect()
        const availableSpaceBelow = window.innerHeight - rect.bottom
        const dropdownHeight = dropdown.offsetHeight

        if (availableSpaceBelow < dropdownHeight) {
            // Not enough space below: open upward
            dropdown.style.top = "auto"
            dropdown.style.bottom = "100%"
            dropdown.style.marginTop = "0"
            dropdown.style.marginBottom = "0.25rem"
        } else {
            // Enough space below: open downward
            dropdown.style.top = "100%"
            dropdown.style.bottom = "auto"
            dropdown.style.marginTop = "0.25rem"
            dropdown.style.marginBottom = "0"
        }
    },

    // Returns true if the input text exactly matches one of the native select's options.
    isValidSelection(text, select) {
        let valid = false

        if (text.trim() !== "") {
            Array.from(select.options).forEach(option => {
                if (option.textContent === text) {
                    valid = true
                }
            })
        }

        return valid
    },

    // Filters dropdown options based on normalized Arabic search text and applies highlighting.
    filterOptions(searchText, dropdown) {
        const normalizedSearch = this.normalizeArabic(searchText.toLowerCase())
        Array.from(dropdown.children).forEach(li => {
            const originalText = li.dataset.originalText
            const normalizedText = this.normalizeArabic(originalText.toLowerCase())
            if (normalizedText.includes(normalizedSearch)) {
                li.classList.remove("hidden")
                if (searchText.trim() !== "") {
                    const regexStr = this.createArabicRegex(searchText)
                    const re = new RegExp("(" + regexStr + ")", "gi")
                    li.innerHTML = originalText.replace(re, '<span class="highlight">$1</span>')
                } else {
                    li.innerHTML = originalText
                }
            } else {
                li.classList.add("hidden")
            }
        })
    },

    // Shows all options (ignoring filtering) and applies highlighting when applicable.
    showAllOptions(searchText, dropdown) {
        Array.from(dropdown.children).forEach(li => {
            li.classList.remove("hidden")
            const originalText = li.dataset.originalText
            if (
                searchText.trim() !== "" &&
                this.normalizeArabic(originalText.toLowerCase()).includes(
                    this.normalizeArabic(searchText.toLowerCase())
                )
            ) {
                const regexStr = this.createArabicRegex(searchText)
                const re = new RegExp("(" + regexStr + ")", "gi")
                li.innerHTML = originalText.replace(re, '<span class="highlight">$1</span>')
            } else {
                li.innerHTML = originalText
            }
        })
    },

    // Updates the active (highlighted) option for keyboard navigation.
    updateActiveOption(visibleOptions, activeIndex) {
        visibleOptions.forEach((li, index) => {
            if (index === activeIndex) {
                li.classList.add("active")
                li.scrollIntoView({block: "nearest"})
            } else {
                li.classList.remove("active")
            }
        })
    },

    // Closes all dropdowns and resets activeIndex reverts placeholder if needed.
    closeAllDropdowns() {
        const containers = document.querySelectorAll(".searchable-select-container")
        containers.forEach(container => {
            const dropdown = container.querySelector(".searchable-select-dropdown")
            if (dropdown) {
                dropdown.classList.add("hidden")
                // Reset inline positioning styles
                dropdown.style.top = ""
                dropdown.style.bottom = ""
                dropdown.style.marginTop = ""
                dropdown.style.marginBottom = ""
            }
            const input = container.querySelector(".searchable-select-input")
            if (input && !input.value) {
                input.placeholder = input.dataset.originalPlaceholder
            }
            container.activeIndex = -1
        })
    },

    // Closes all dropdowns except for the current container.
    closeAllExcept(currentContainer) {
        const containers = document.querySelectorAll(".searchable-select-container")
        containers.forEach(container => {
            if (container !== currentContainer) {
                const dropdown = container.querySelector(".searchable-select-dropdown")
                if (dropdown) {
                    dropdown.classList.add("hidden")
                    dropdown.style.top = ""
                    dropdown.style.bottom = ""
                    dropdown.style.marginTop = ""
                    dropdown.style.marginBottom = ""
                }
                const input = container.querySelector(".searchable-select-input")
                if (input && !input.value) {
                    input.placeholder = input.dataset.originalPlaceholder
                }
                container.activeIndex = -1
            }
        })
    },

    // Escapes RegExp special characters in a string.
    escapeRegExp(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    },

    // Retrieves the localized "Type to search" text from a meta tag.
    getLocalizedTypeToSearch() {
        const meta = document.querySelector('meta[name="localized-type-to-search"]')
        return meta ? meta.getAttribute("content") : "Type to search"
    },

    // Normalizes Arabic text so that variants are treated equally.
    normalizeArabic(text) {
        return text
            .replace(/[أإآ]/g, "ا")
            .replace(/[يىئ]/g, "ي")
            .replace(/ؤ/g, "و")
    },

    // Creates a regex string that matches Arabic variants for each character.
    createArabicRegex(searchText) {
        const mapping = {
            "ا": "[اأإآ]",
            "أ": "[اأإآ]",
            "إ": "[اأإآ]",
            "آ": "[اأإآ]",
            "ي": "[يىئ]",
            "ى": "[يىئ]",
            "ئ": "[يىئ]",
            "و": "[وؤ]",
            "ؤ": "[وؤ]"
        }
        let regexStr = ""
        for (let char of searchText) {
            const lowerChar = char.toLowerCase()
            if (mapping[lowerChar]) {
                regexStr += mapping[lowerChar]
            } else {
                regexStr += this.escapeRegExp(char)
            }
        }
        return regexStr
    }
}

document.addEventListener("DOMContentLoaded", function () {
    SearchableSelect.init()
})
